/**
 * FDV — barra de progreso de regalos del carrito (Decoración · C2).
 * Ver docs/migration/15-carrito-regalos.md §5.
 *
 * Presentacional. Se suscribe a `fdv:gifts:state` (contrato B del motor C1) y
 * pinta una **escalera de recompensas**:
 *   - titular: cuánto falta para el próximo premio (o "todo desbloqueado")
 *   - barra con marcador "tú: $X"
 *   - un paso por cada oferta `kind: 'threshold'` con su nombre + monto + estado
 *   - chips ✓ para promos por código / enlace (`kind: 'binary'` desbloqueadas)
 *   - chip de aviso para ofertas con `error`
 *
 * NO muta el carrito. NO conoce el metaobjeto. Si truena, el carrito y el motor
 * siguen intactos. Puede haber N instancias (drawer + página /cart).
 *
 * Fix (2026-09-08): un `morphSection` de Horizon puede re-escribir este elemento
 * a su estado inicial (`hidden`, vacío) DESPUÉS de que renderizamos, sin
 * re-ejecutar `connectedCallback`. Un MutationObserver lo detecta y re-renderiza.
 */

const money = (cents, currency) => {
  const v = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || 'MXN',
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    }).format(v);
  } catch {
    return `$${Math.round(v)}`;
  }
};

const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Nombre corto del premio para el paso de la escalera. */
function rewardLabel(s) {
  if (s.rewardLabel) return s.rewardLabel;
  if (s.mechanism === 'free_shipping_message') return 'Envío gratis';
  if (s.mystery) return 'Regalo sorpresa';
  let t = (s.giftLineLabel || 'Tu regalo')
    .replace(/^[^\p{L}]+/u, '')
    .replace(/\s*(¡?gratis!?|de regalo)\s*/giu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Tu regalo';
}

class FdvGiftProgress extends HTMLElement {
  connectedCallback() {
    this._onState = (e) => this.render(e.detail?.state, e.detail?.cart);
    document.addEventListener('fdv:gifts:state', this._onState);

    // Recuperación tras un morph externo que nos deje hidden/vacío.
    this._mo = new MutationObserver(() => {
      if (this._writing) return;
      const g = window.FDV?.gifts;
      if (g?.state?.length && (this.hidden || !this.firstElementChild)) this.render(g.state, g.cart);
    });
    this._mo.observe(this, { attributes: true, attributeFilter: ['hidden'], childList: true });

    const g = window.FDV?.gifts;
    if (g?.state?.length) this.render(g.state, g.cart);
    else this.hidden = true;
  }

  disconnectedCallback() {
    document.removeEventListener('fdv:gifts:state', this._onState);
    this._mo?.disconnect();
  }

  get showBar() {
    return this.dataset.showBar !== 'false';
  }
  get showChips() {
    return this.dataset.showChips !== 'false';
  }
  get context() {
    return this.dataset.context || 'page';
  }

  render(state, cart) {
    if (!Array.isArray(state)) return;
    const currency = window.FDV?.gifts?.currency || cart?.currency || 'MXN';
    // Subtotal de mercancía después de descuentos y SIN envío — la misma base que
    // usa el motor para calificar el umbral y Shopify para el envío gratis. NO
    // `cart.total_price` (puede traer el envío sumado). Ver `merchSubtotal` en
    // fdv-gift-engine.js.
    const c = cart || window.FDV?.gifts?.cart;
    const subtotal = c ? (window.FDV?.gifts?.merchSubtotal?.(c) ?? c.items_subtotal_price ?? 0) : 0;

    const thresholds = state
      .filter((s) => s.kind === 'threshold' && s.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);

    // Chips ✓ para promos por código / enlace. Los pares producto→regalo
    // (`product_in_cart`) NO llevan chip: ya tienen su línea de regalo + el toast.
    const chips = state.filter(
      (s) => s.kind === 'binary' && s.status === 'unlocked' && s._offer?.triggerType !== 'product_in_cart'
    );
    const errors = state.filter((s) => s.error);

    let html = '';
    if (this.showBar && thresholds.length) html += this.#ladder(thresholds, subtotal, currency);
    if (this.showChips && chips.length) html += this.#chips(chips);
    if (errors.length) html += this.#errors(errors);

    // Escrituras idempotentes + descartar los registros propios: el observer los
    // entrega async, DESPUÉS de `_writing = false`, así que el flag solo no basta.
    // `el.hidden = true` sobre un elemento ya oculto igual encola un registro
    // (setAttribute siempre registra) → callback → render() → bucle infinito que
    // congela la página (state no vacío pero sin HTML que pintar).
    this._writing = true;
    if (!html) {
      if (!this.hidden) this.hidden = true;
      if (this.firstChild) this.innerHTML = '';
    } else {
      if (this.hidden) this.hidden = false;
      this.innerHTML = `<div class="fdv-gift-progress__inner">${html}</div>`;
    }
    this._mo?.takeRecords();
    this._writing = false;
  }

  #ladder(thresholds, subtotal, currency) {
    const next = thresholds.find((s) => s.status !== 'unlocked');

    // Titular
    let headline;
    if (next) {
      headline =
        next.progressText ||
        `Te faltan <b>${esc(money(next.remainingAmount || 0, currency))}</b> para desbloquear ${esc(rewardLabel(next).toLowerCase())}`;
    } else {
      const names = thresholds.map((s) => rewardLabel(s).toLowerCase());
      const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}` : names[0];
      headline = `✓ Todo desbloqueado — ${esc(list)}`;
      // En el cart drawer, todo cumplido → las tarjetas de la escalera solo
      // repiten "¡Listo!" y ocupan ~200 px, comprimiendo la lista de productos
      // (ver docs/migration/15-carrito-regalos.md §5.6). El titular ✓ basta.
      // En /cart hay espacio de sobra → se mantiene la escalera con sus ✓.
      if (this.context === 'drawer') {
        return `<p class="fdv-gift-progress__headline fdv-gift-progress__headline--done" aria-live="polite">${headline}</p>`;
      }
    }

    // Un paso por umbral, con su propia barrita de progreso y su estado.
    let lo = 0;
    const steps = thresholds
      .map((s) => {
        const hi = s.threshold;
        const fill = Math.max(0, Math.min(100, ((subtotal - lo) / (hi - lo || 1)) * 100));
        lo = hi;
        const done = s.status === 'unlocked';
        const icon = done ? '✓' : s.mystery ? '🎁' : '🔒';
        const status = done
          ? '¡Listo!'
          : s.remainingAmount
            ? `Te faltan ${esc(money(s.remainingAmount, currency))}`
            : 'Casi lo tienes';
        return `<li class="fdv-gift-progress__step${done ? ' is-done' : ''}${s.mystery ? ' is-mystery' : ''}">
          <span class="fdv-gift-progress__step-head">
            <span class="fdv-gift-progress__step-icon" aria-hidden="true">${icon}</span>
            <span class="fdv-gift-progress__step-name">${esc(rewardLabel(s))}</span>
          </span>
          <span class="fdv-gift-progress__step-bar"><span class="fdv-gift-progress__step-fill" style="width:${fill}%"></span></span>
          <span class="fdv-gift-progress__step-status">${status}</span>
        </li>`;
      })
      .join('');

    return `
      <p class="fdv-gift-progress__headline" aria-live="polite">${headline}</p>
      <ul class="fdv-gift-progress__steps"
          role="progressbar" aria-valuemin="0"
          aria-valuemax="${thresholds[thresholds.length - 1].threshold}"
          aria-valuenow="${Math.round(subtotal)}">${steps}</ul>`;
  }

  #chips(chips) {
    const items = chips
      .map((s) => `<li class="fdv-gift-progress__chip">✓ ${esc(s.unlockedHeadline || 'Regalo aplicado')}</li>`)
      .join('');
    return `<ul class="fdv-gift-progress__chips">${items}</ul>`;
  }

  #errors(errors) {
    const items = errors
      .map(
        (s) =>
          `<li class="fdv-gift-progress__chip fdv-gift-progress__chip--error">⚠ ${esc(
            s.unlockedHeadline || s.internalName || 'Promoción no disponible'
          )}</li>`
      )
      .join('');
    return `<ul class="fdv-gift-progress__chips">${items}</ul>`;
  }
}

if (!customElements.get('fdv-gift-progress')) {
  customElements.define('fdv-gift-progress', FdvGiftProgress);
}
